import type { CategoryId } from '@/shared/constants/category-colors';
import { ROUTES } from '@/shared/config/routes';
import { i18n } from '@/shared/i18n';
import { REELS_CREATOR_TOOL_ID } from '@/shared/utils/reels-creator';

export const CONTENT_STUDIO_TOOL_ID = 'content-studio';
// eslint-disable-next-line i18next/no-literal-string -- agent registry id
export const IQ_TOOL_ID = 'iq';

/**
 * Synthetic "managed tools" — non-agent tools (e.g. Content Test) that are
 * gated per-workspace through the agent ACL machinery. This registry is the
 * single source of id + display metadata, consumed by both the admin Tools
 * list (`features/admin`) and the Springboard tile (`features/springboard`).
 *
 * Keep in sync with the backend `MANAGED_TOOL_IDS`
 * (apps/api/src/access-control/managed-tools.constants.ts).
 */
export interface ManagedTool {
  id: string;
  name: string;
  description: string;
  tag: CategoryId;
  /**
   * In-app route this tool opens. Managed tools are pages, not chat agents, so
   * consumers (tool grid, top dropdown) navigate here instead of launching an
   * agent by id.
   */
  route: string;
}

export const MANAGED_TOOLS: ManagedTool[] = [
  {
    id: 'test-content',
    // Getters, not values: resolving per read keeps the label following a language
    // change, and keeps this module-scope array free of import-time translation.
    get name() {
      return i18n.t('tools:managedTools.contentTest.name');
    },
    get description() {
      return i18n.t('tools:managedTools.contentTest.description');
    },
    tag: 'plan',
    route: ROUTES.TOOLS_CONTENT_TEST,
  },
  {
    id: 'focus-groups',
    get name() {
      return i18n.t('tools:managedTools.focusGroups.name');
    },
    get description() {
      return i18n.t('tools:managedTools.focusGroups.description');
    },
    tag: 'plan',
    route: ROUTES.TOOLS_FOCUS_GROUPS,
  },
  {
    id: 'nacb-intake',
    get name() {
      return i18n.t('tools:managedTools.nacbIntake.name');
    },
    get description() {
      return i18n.t('tools:managedTools.nacbIntake.description');
    },
    tag: 'plan',
    route: ROUTES.TOOLS_NACB_INTAKE,
  },
  {
    id: IQ_TOOL_ID,
    get name() {
      return i18n.t('tools:managedTools.iq.name');
    },
    get description() {
      return i18n.t('tools:managedTools.iq.description');
    },
    tag: 'plan',
    route: ROUTES.IQ,
  },
  {
    id: CONTENT_STUDIO_TOOL_ID,
    get name() {
      return i18n.t('tools:managedTools.contentStudio.name');
    },
    get description() {
      return i18n.t('tools:managedTools.contentStudio.description');
    },
    tag: 'create',
    route: ROUTES.CONTENT_STUDIO,
  },
  {
    id: REELS_CREATOR_TOOL_ID,
    get name() {
      return i18n.t('tools:managedTools.reelsCreator.name');
    },
    get description() {
      return i18n.t('tools:managedTools.reelsCreator.adminDescription');
    },
    tag: 'create',
    route: ROUTES.REELS_CREATOR,
  },
];

export const MANAGED_TOOL_IDS: string[] = MANAGED_TOOLS.map((t) => t.id);

export const getManagedTool = (id: string): ManagedTool | undefined =>
  MANAGED_TOOLS.find((t) => t.id === id);
